import { action, computed, makeObservable, observable } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { httpStatusCodes } from "mrb";
import { localizationService } from "common/localization";
import { isNil } from "lodash";
import buildContentTreeWithRevenue from "./buildContentTreeWithRevenue";
import { contentNodePreviewUrl } from "common/utils/contentPreviewLink";

class ContentTreeViewStore extends MrbBaseViewStore {
    @observable.ref nodes = [];
    @observable expandedIds = new Map();
    @observable producers = null;
    @observable selectedProducerId = null;
    @observable.ref revenuePopupNode = null;

    @computed get isEmpty() {
        return this.nodes.length === 0;
    }

    // Flattens the tree into the rows currently visible (collapsed subtrees are excluded),
    // so the template can render them in a single virtualized list. Recomputes when the
    // data or any expand/collapse state changes.
    @computed get visibleRows() {
        const rows = [];
        const walk = (list, depth) => {
            for (const node of list) {
                const hasChildren = !!(node.children && node.children.length > 0);
                const expanded = node.type !== "track" && node.type !== "episode" && this.isExpanded(node.id);
                rows.push({
                    id: node.id,
                    type: node.type,
                    title: node.title,
                    revenue: node.revenue,
                    totalRevenue: node.totalRevenue,
                    revenueMatched: node.revenueMatched,
                    revenuePercentage: node.revenuePercentage,
                    coverKind: node.coverKind,
                    coverImageId: node.coverImageId,
                    // Ids/slug the content-link builder needs (track→albumId, episode→podcastId,
                    // podcast/group/episode→categorySlug).
                    albumId: node.albumId,
                    podcastId: node.podcastId,
                    categorySlug: node.categorySlug,
                    // Whether the copy link may be shown (node + all ancestors enabled).
                    linkable: node.linkable,
                    depth,
                    hasChildren,
                    expanded,
                });
                if (expanded && hasChildren) {
                    walk(node.children, depth + 1);
                }
            }
        };
        walk(this.nodes, 0);
        return rows;
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;
        this.isExpanded = this.isExpanded.bind(this);
        this.getCoverUrl = this.getCoverUrl.bind(this);
        this.getContentLink = this.getContentLink.bind(this);
        // copyContentLink is @action.bound (already bound + read-only) — do not re-bind it here.
    }

    async onInit() {
        const producers = await this.routeStore.getProducers(new AbortController());
        this.setProducers(producers);
        if (!Array.isArray(producers) || producers.length === 0) {
            this.setNodes([]);
            return;
        }
        const parentSelectedId = this.rootStore.producerPreviewViewStore?.selectedProducerId;
        const initialId =
            parentSelectedId && producers.some((p) => p.id === parentSelectedId)
                ? parentSelectedId
                : producers[0].id;
        this.rootStore.producerPreviewViewStore?.setSelectedProducerId(initialId);
        // An account may be assigned to multiple producers (e.g. an artist + a podcaster);
        // load the first by default and let the user switch via the producer selector.
        await this.loadProducerContent(initialId);

        this.reaction(
            () => this.rootStore.producerPreviewViewStore?.selectedProducerId,
            (newId) => { if (newId) this.selectProducer(newId); }
        );
    }

    // Loads the content tree + revenue for a single producer and merges them into the
    // render-ready node model. Used both on init and on producer switch.
    async loadProducerContent(producerId) {
        this.setSelectedProducerId(producerId);
        try {
            const abortController = new AbortController();
            const [tree, revenueMap] = await Promise.all([
                this.routeStore.getContentTree(producerId, abortController),
                this.routeStore.getRevenueMap(producerId, abortController),
            ]);
            const revenueList = this.normalizeRevenue(revenueMap);
            this.setNodes(buildContentTreeWithRevenue(tree, revenueList));
        } catch (error) {
            if (error && error.statusCode === httpStatusCodes.CanceledRequest) {
                return;
            }
            this.setNodes([]);
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.PRODUCER.CONTENT_TREE.LOAD_ERROR"),
                error
            );
        }
    }

    @action.bound
    setProducers(producers) {
        this.producers = Array.isArray(producers) ? producers : null;
    }

    @action.bound
    setSelectedProducerId(producerId) {
        this.selectedProducerId = producerId;
    }

    @action.bound
    async selectProducer(producerId) {
        if (producerId === this.selectedProducerId) {
            return;
        }
        this.rootStore.producerPreviewViewStore?.setSelectedProducerId(producerId);
        this.loaderStore.suspend();
        try {
            await this.loadProducerContent(producerId);
        } finally {
            this.loaderStore.resume();
        }
    }

    // The revenue endpoint may return a bare array or a REST collection ({ item: [...] }).
    normalizeRevenue(revenueMap) {
        if (Array.isArray(revenueMap)) {
            return revenueMap;
        }
        if (revenueMap && Array.isArray(revenueMap.item)) {
            return revenueMap.item;
        }
        return [];
    }

    @action.bound
    setNodes(nodes) {
        this.nodes = nodes;
        const expanded = new Map();
        this.seedExpandState(nodes, expanded);
        this.expandedIds = expanded;
    }

    // Collections and podcast groups expanded by default; albums and podcasts collapsed by
    // default; tracks and episodes are leaves.
    seedExpandState(nodes, expanded) {
        for (const node of nodes) {
            if (node.type === "collection" || node.type === "podcastGroup") {
                expanded.set(node.id, true);
            } else if (node.type === "album" || node.type === "podcast") {
                expanded.set(node.id, false);
            }
            if (node.children && node.children.length > 0) {
                this.seedExpandState(node.children, expanded);
            }
        }
    }

    isExpanded(id) {
        return this.expandedIds.get(id) === true;
    }

    // Builds the cover thumbnail url for a row from its kind + cover image id, using the shared
    // CoverImageFactory (the same path the rest of the app uses for artist/album/podcast covers).
    getCoverUrl(coverKind, coverImageId, size = 48) {
        if (isNil(coverImageId)) {
            return null;
        }
        const coverImageFactory = this.rootStore.coverImageFactory;
        if (coverKind === "artist") {
            return coverImageFactory.createNakiNigunArtistCoverImageUrl(coverImageId, size, size);
        }
        if (coverKind === "podcast") {
            return coverImageFactory.createPodcastCoverImageUrl(coverImageId, size, size);
        }
        return coverImageFactory.createNakiNigunAlbumCoverImageUrl(coverImageId, size, size);
    }

    // Returns the shareable preview link for a row, or null when the row can't be linked
    // (e.g. a podcast/episode missing its category slug). The template uses null to hide the action.
    getContentLink(row) {
        return contentNodePreviewUrl(row);
    }

    // Copies the row's preview link to the clipboard and notifies the user.
    @action.bound
    async copyContentLink(row) {
        const url = contentNodePreviewUrl(row);
        if (!url) {
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            this.rootStore.notificationStore.success(
                localizationService.t("APPLICATION.PRODUCER.CONTENT_TREE.LINK_COPIED")
            );
        } catch (error) {
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.PRODUCER.CONTENT_TREE.LINK_COPY_ERROR"),
                error
            );
        }
    }

    @action.bound
    toggleNode(id) {
        const current = this.expandedIds.get(id) === true;
        const next = new Map(this.expandedIds);
        next.set(id, !current);
        this.expandedIds = next;
    }

    @action.bound
    openRevenuePopup(row) {
        this.revenuePopupNode = row;
    }

    @action.bound
    closeRevenuePopup() {
        this.revenuePopupNode = null;
    }
}

export default ContentTreeViewStore;
